import { ErrorSidePanel } from '@/components/errors';
import { useForm } from '@deadcow-enterprises/react-forms';
import {
  logger,
  ModuleName,
  Screen,
  templates,
  utils,
  widgets,
  type SkinPageConfig,
} from '@nosinovacao/nosid-mfe-common';
import { createFileRoute, useLocation } from '@tanstack/react-router';
import { useEffect } from 'react';
import z from 'zod';
import { kidProfileSchema } from '../-model/kid-profile.schema';
import { profileFormLoaderFn, type ProfileForm } from '../-model/profile-form';
import { useCreateProfile } from './-hooks/add-profile.hook';
import { type CreateProfileProps } from './-model/model';
import { CreateTvProfileLight, CreateTvProfileWooDark, CreateTvProfileWoo } from './-skins';

export const Route = createFileRoute('/(tv)/tv/$id/(tv-profiles)/_sheet/profiles/create/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  loader: profileFormLoaderFn,
  staticData: {
    module: ModuleName.Tv,
    screen: Screen.CreateProfile,
  },
  validateSearch: kidProfileSchema,
});

function RouteComponent() {
  const { kids } = Route.useSearch();
  const initialValue = Route.useLoaderData();
  const { hash } = useLocation();
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const { mutateAsync } = useCreateProfile();
  const form = useForm<ProfileForm>(
    {
      type: {
        value: kids ? 'Kids' : 'NextGen',
      },
      hasPin: {
        value: initialValue?.hasPin ?? false,
      },
      pin: {
        value: initialValue?.pin ?? '',
        validator: ({ hasPin }) =>
          hasPin
            ? z.string().min(widgets.PinCodeLength, 'TvProfiles.CreateProfile.Errors.PinRequired')
            : z.any(),
      },
      confirmPin: {
        value: initialValue?.confirmPin ?? '',
        validator: ({ hasPin, pin }) =>
          hasPin && pin && pin.length === widgets.PinCodeLength
            ? z.string().refine((val) => val === pin, 'TvProfiles.CreateProfile.Errors.PinMismatch')
            : z.any(),
      },
      name: {
        value: initialValue?.name ?? '',
        validator: z.string().min(1, 'TvProfiles.CreateProfile.Errors.NameRequired'),
      },
      ageRating: {
        value: initialValue?.ageRating ?? (kids ? widgets.AgeRatings[0].TopSegmentKey : 'Todos'),
      },
      avatar: {
        value: initialValue?.avatar ?? null,
        validator: z.object(
          {
            Id: z.string(),
            ImageId: z.string(),
            ImageUri: z.union([z.string(), z.undefined()]),
          },
          { error: 'TvProfiles.CreateProfile.Errors.AvatarRequired' },
        ),
      },
    },
    {
      focusOnError: true,
      onError: logger.logFormErrors.bind(logger),
    },
  );

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!form.value || form.pristine) return;
      const compressed = await utils.compressToBase64Url(JSON.stringify(form.value));
      navigate({ hash: compressed, replace: true, search: { kids } });
    }, 250);

    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(form.value)]);

  async function onSubmit(value: ProfileForm) {
    await mutateAsync({ profile: value, sa: id });
    navigate({ to: '../../profiles' });
  }

  const props: CreateProfileProps = {
    isKidsProfile: kids,
    formValueHash: hash,
    form,
    onSubmit,
    sa: id,
  };
  return templates.getSkinSelector(skinConfig(props));
}

export function skinConfig(props: CreateProfileProps): SkinPageConfig {
  return {
    skins: [
      {
        selector: 'isWooTvTemplate',
        component: <CreateTvProfileWooDark {...props} />,
      },
      {
        selector: 'isWooTemplate',
        component: <CreateTvProfileWoo {...props} />,
      },
    ],
    defaultValue: <CreateTvProfileLight {...props} />,
  };
}
